import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Badge, Flex, Text, Box,
  HStack, Select, Input, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import deportesApi, { Torneo } from '../../../api/deportesApi';
import FormularioTorneo, { ESTATUS_TORNEO } from './FormularioTorneo';
import ModalGestionTorneo from './ModalGestionTorneo';
import { useCatalogos, Paginacion, fmtFecha, ROJO, ROJO_HOVER } from '../_shared';

const ESTATUS_COLOR: Record<string, string> = {
  Programado: 'cyan', 'En proceso': 'yellow', Finalizado: 'green', Cancelado: 'red',
};

const TablaTorneos: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { disciplinas, categorias, localidades } = useCatalogos();

  const { isOpen: isVer, onOpen: onVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isEditar, onOpen: onEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isNuevo, onOpen: onNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isGestion, onOpen: onGestion, onClose: onCloseGestion } = useDisclosure();
  const { isOpen: isEliminar, onOpen: onEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [sel, setSel] = useState<Torneo | null>(null);
  const [data, setData] = useState<Torneo[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // filtros: nombre, disciplina, categoria, fecha, localidad, responsable, estatus
  const [fNombre, setFNombre] = useState('');
  const [fDisciplina, setFDisciplina] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fFecha, setFFecha] = useState('');
  const [fLocalidad, setFLocalidad] = useState('');
  const [fResponsable, setFResponsable] = useState('');
  const [fEstatus, setFEstatus] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await deportesApi.listarTorneos({
        nombre: fNombre, disciplinaId: fDisciplina, categoriaId: fCategoria,
        fecha: fFecha, localidadId: fLocalidad, responsable: fResponsable, estatus: fEstatus,
      });
      setData(res || []);
    } catch { setData([]); } finally { setCargando(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fNombre, fDisciplina, fCategoria, fFecha, fLocalidad, fResponsable, fEstatus]);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = data.slice(indexPrimero, indexUltimo);

  const abrir = async (item: Torneo, accion: 'ver' | 'editar' | 'gestion') => {
    try { setSel(await deportesApi.obtenerTorneo(item.id)); }
    catch { setSel(item); }
    if (accion === 'ver') onVer();
    else if (accion === 'editar') onEditar();
    else onGestion();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminar = async () => {
    if (!sel) return;
    setProcesando(true);
    try {
      await deportesApi.eliminarTorneo(sel.id);
      toast({ title: 'Torneo eliminado', status: 'success', duration: 3000 });
      onCloseEliminar(); fetchData();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally { setProcesando(false); }
  };

  const exportExcel = () => {
    const filas = data.map((t) => ({
      Nombre: t.nombre, Disciplina: t.disciplina?.nombre || '', Categoría: t.categoria?.nombre || '',
      Rama: t.rama?.nombre || '', 'Fecha inicio': fmtFecha(t.fechaInicio), 'Fecha conclusión': fmtFecha(t.fechaConclusion),
      Sede: t.sede || '', Localidad: t.localidad || '', Responsable: t.responsable || '', Estatus: t.estatus || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Torneos');
    XLSX.writeFile(wb, `Torneos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const reportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const blob = await deportesApi.reporteTorneosPdf({
        nombre: fNombre, disciplinaId: fDisciplina, categoriaId: fCategoria,
        fecha: fFecha, localidadId: fLocalidad, responsable: fResponsable, estatus: fEstatus,
      });
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo generar el PDF.', status: 'error', duration: 4000 });
    } finally { setGenerandoPdf(false); }
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Input placeholder="Buscar por nombre..." value={fNombre} onChange={(e) => setFNombre(e.target.value)} size="sm" w={{ base: 'full', md: '190px' }} />
          <Select placeholder="Disciplina" value={fDisciplina} onChange={(e) => setFDisciplina(e.target.value)} size="sm" w="140px">
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
          <Select placeholder="Categoría" value={fCategoria} onChange={(e) => setFCategoria(e.target.value)} size="sm" w="130px">
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
          <Select placeholder="Localidad" value={fLocalidad} onChange={(e) => setFLocalidad(e.target.value)} size="sm" w="140px">
            {localidades.map((l: any) => <option key={l.id} value={l.id}>{l.descripcion}</option>)}
          </Select>
          <Input placeholder="Responsable" value={fResponsable} onChange={(e) => setFResponsable(e.target.value)} size="sm" w="130px" />
          <Input type="date" value={fFecha} onChange={(e) => setFFecha(e.target.value)} size="sm" w="150px" />
          <Select placeholder="Estatus" value={fEstatus} onChange={(e) => setFEstatus(e.target.value)} size="sm" w="140px">
            {ESTATUS_TORNEO.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={exportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<DownloadIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={reportePdf} isLoading={generandoPdf} loadingText="Generando...">Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={onNuevo}>Nuevo torneo</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={ROJO}>
            <Tr>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Disciplina</Th>
              <Th color="white" fontSize="xs">Categoría</Th>
              <Th color="white" fontSize="xs">Fechas</Th>
              <Th color="white" fontSize="xs">Sede / Localidad</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron torneos</Text></Td></Tr>
            ) : itemsPagina.map((t, idx) => (
              <Tr key={t.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                <Td maxW="200px"><Text noOfLines={1} fontSize="sm" fontWeight="medium">{t.nombre}</Text></Td>
                <Td>{t.disciplina?.nombre || '—'}</Td>
                <Td>{t.categoria?.nombre || '—'}{t.rama?.nombre ? ` / ${t.rama.nombre}` : ''}</Td>
                <Td fontSize="xs">{fmtFecha(t.fechaInicio)}<br />{fmtFecha(t.fechaConclusion)}</Td>
                <Td fontSize="xs">{t.sede || '—'}<br /><Text color="gray.500">{t.localidad || ''}</Text></Td>
                <Td>{t.responsable || '—'}</Td>
                <Td><Badge colorScheme={ESTATUS_COLOR[t.estatus || ''] || 'gray'} borderRadius="full" px={2}>{t.estatus}</Badge></Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrir(t, 'ver')}>Ver</Button>
                    <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrir(t, 'editar')}>Editar</Button>
                    <Button size="xs" bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={() => abrir(t, 'gestion')}>Gestionar</Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSel(t); onEliminar(); }}>Eliminar</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Paginacion currentPage={currentPage} totalPages={totalPages} total={data.length} indexPrimero={indexPrimero} indexUltimo={indexUltimo} onChange={setCurrentPage} />

      <Modal isOpen={isVer} onClose={onCloseVer} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Detalle del Torneo</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioTorneo modo="view" data={sel} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditar} onClose={onCloseEditar} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Editar Torneo</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioTorneo modo="edit" data={sel} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNuevo} onClose={onCloseNuevo} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Nuevo Torneo</ModalHeader>
          <ModalBody py={5}><FormularioTorneo modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <ModalGestionTorneo isOpen={isGestion} onClose={onCloseGestion} torneo={sel} />

      <AlertDialog isOpen={isEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ROJO}>Eliminar torneo</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar el torneo <strong>{sel?.nombre}</strong>? Esta acción lo marcará como inactivo.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaTorneos;
